import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { AppModeService } from '@core/services/app-mode.service';
import { PaletaService } from '@shared/services/paleta.service';
import { PublicCongressPresentationConfig } from '../models/congress-presentation-config.model';
import { STORAGE_KEYS, StorageService } from '@core/services/storage.service';

@Injectable({
  providedIn: 'root'
})
export class CongresoThemeService {
  private readonly appModeService = inject(AppModeService);
  private readonly paletaService = inject(PaletaService);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly storageService = inject(StorageService);

  private readonly _isDarkTheme = signal<boolean>(this.getInitialTheme());

  readonly isDarkTheme = this._isDarkTheme.asReadonly();

  readonly themeMode = computed<'light' | 'dark'>(() =>
    this.isDarkTheme() ? 'dark' : 'light'
  );

  readonly themeButtonLabel = computed(() =>
    this.isDarkTheme() ? 'Modo claro' : 'Modo oscuro'
  );

  readonly config = computed<PublicCongressPresentationConfig | null>(() => {
    const congresoData = this.appModeService.congresoData();
    const raw = congresoData?.config;

    if (!raw) return null;

    try {
      if (typeof raw === 'string') {
        return JSON.parse(raw) as PublicCongressPresentationConfig;
      }

      return raw as PublicCongressPresentationConfig;
    } catch (e) {
      console.error('[CongresoThemeService] Error parseando config JSON:', e);
      console.error('[CongresoThemeService] raw value was:', raw);

      return null;
    }
  });

  readonly templateId = computed(() => this.config()?.templateId ?? null);

  readonly branding = computed(() => this.config()?.branding ?? null);

  readonly paletteKey = computed(() =>
    this.config()?.branding?.paletteKey ?? null
  );

  readonly cssVariables = computed<Record<string, string>>(() =>
    this.paletaService.getCongresoCssVariables(this.paletteKey())
  );

  readonly primePrimaryVariables = computed<Record<string, string>>(() =>
    this.paletaService.getPrimePrimaryCssVariables(this.paletteKey())
  );

  readonly allCssVariables = computed<Record<string, string>>(() => ({
    ...this.cssVariables(),
    ...this.primePrimaryVariables()
  }));

  constructor() {
    effect(() => {
      if (!this.isBrowser()) return;

      const mode = this.themeMode();
      const isDark = mode === 'dark';
      const root = this.document.documentElement;

      root.classList.toggle('dark-theme', isDark);
      root.setAttribute('data-theme', mode);
    });

    /**
     * Puente de branding del congreso.
     *
     * CongresoThemeService ya no calcula colores.
     * Solo pide las variables al PaletaService y las aplica en :root.
     */
    effect(() => {
      if (!this.isBrowser()) return;

      const root = this.document.documentElement;
      const vars = this.allCssVariables();

      this.paletaService.congresoThemeVars.forEach(key => {
        root.style.removeProperty(key);
      });

      if (Object.keys(vars).length === 0) return;

      Object.entries(vars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    });
  }

  toggleTheme(): void {
    this.setTheme(!this.isDarkTheme(), true);
  }

  setTheme(isDark: boolean, guardarPreferencia = true): void {
    this._isDarkTheme.set(isDark);

    if (guardarPreferencia) {
      this.guardarPreferenciaTema(isDark);
    }
  }

  private guardarPreferenciaTema(isDark: boolean): void {
    if (!this.isBrowser()) return;

    this.storageService.setItem(STORAGE_KEYS.DARK_MODE_ENABLED, isDark);
  }

  applyCssVariables(el: HTMLElement): void {
    const vars = this.allCssVariables();

    Object.entries(vars).forEach(([key, value]) => {
      el.style.setProperty(key, value);
    });
  }

  removeCssVariables(el: HTMLElement): void {
    const vars = this.allCssVariables();

    Object.keys(vars).forEach(key => {
      el.style.removeProperty(key);
    });
  }

  private getInitialTheme(): boolean {
    if (!this.isBrowser()) {
      return false;
    }

    const saved = this.storageService.getItem(STORAGE_KEYS.DARK_MODE_ENABLED);

    if (typeof saved === 'boolean') {
      return saved;
    }

    // Migración desde la antigua clave del área pública ('congreso-theme')
    try {
      const antiguo = localStorage.getItem('congreso-theme');

      if (antiguo === 'dark') {
        this.storageService.setItem(STORAGE_KEYS.DARK_MODE_ENABLED, true);
        localStorage.removeItem('congreso-theme');
        return true;
      }

      if (antiguo === 'light') {
        this.storageService.setItem(STORAGE_KEYS.DARK_MODE_ENABLED, false);
        localStorage.removeItem('congreso-theme');
        return false;
      }
    } catch {
      // Sin almacenamiento disponible: se usa el tema del SO.
    }

    // Si no hay preferencia guardada usa la del SO
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
 * Actualiza el favicon del documento con la imagen base64 de la configuración.
 * Si no hay URL, restaura el favicon por defecto.
 */
  setFavicon(faviconUrl: string | null | undefined): void {
    const selector = 'link[rel="icon"]';
    const existente = document.querySelector<HTMLLinkElement>(selector);

    if (faviconUrl) {
      if (existente) {
        existente.href = faviconUrl;
      } else {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = faviconUrl;
        document.head.appendChild(link);
      }
    } else if (existente) {
      existente.remove();
    }
  }
}
